// Mirrors aryaconnectui-2.0 sms_encoding.dart and aryaconnect MessageUtils.CalculateMessageCredits,
// so the count shown here is the one the provider splits and the credits are charged on.
const GSM7_SINGLE_CAPACITY = 160;
const GSM7_CONCAT_CAPACITY = 153;
const UCS2_SINGLE_CAPACITY = 70;
const UCS2_CONCAT_CAPACITY = 67;

export const MAX_SMS_MESSAGES = 7;

// aryaconnect appends the consent footer as body + Environment.NewLine x2 on Linux hosts.
export const CONSENT_SEPARATOR = '\n\n';

const GSM7_BASIC = new Set(
  Array.from(
    '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?' +
      '¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà'
  )
);

const GSM7_EXTENDED = new Set(Array.from('\f^{}\\[~]|€'));

const PLAIN_TEXT_REPLACEMENTS = {
  '‘': "'",
  '’': "'",
  '‚': "'",
  '‛': "'",
  '′': "'",
  '´': "'",
  '`': "'",
  '“': '"',
  '”': '"',
  '„': '"',
  '‟': '"',
  '″': '"',
  '«': '"',
  '»': '"',
  '‐': '-',
  '‑': '-',
  '‒': '-',
  '–': '-',
  '—': '-',
  '―': '-',
  '−': '-',
  '\u00A0': ' ',
  '\u2007': ' ',
  '\u2008': ' ',
  '\u2009': ' ',
  '\u200A': ' ',
  '\u202F': ' ',
  '\u3000': ' ',
  '\u200B': '',
  '\u200C': '',
  '\u200D': '',
  '\uFEFF': '',
  '…': '...',
  '•': '-',
  '·': '.',
  '™': '(TM)',
  '®': '(R)',
  '©': '(C)',
  '½': '1/2',
  '¼': '1/4',
  '¾': '3/4',
};

const EMPTY_ANALYSIS = Object.freeze({
  isUnicode: false,
  characters: 0,
  messages: 0,
  specialCharacters: [],
});

function isGsm7Character(character) {
  return GSM7_BASIC.has(character) || GSM7_EXTENDED.has(character);
}

// Emoji and other astral characters are two UTF-16 units, and UCS-2 bills per unit.
function getCharacterCost(character, isUnicode) {
  if (isUnicode) return character.length;
  return GSM7_EXTENDED.has(character) ? 2 : 1;
}

export function countSmsCharacters(text, isUnicode) {
  return Array.from(text ?? '').reduce((sum, character) => sum + getCharacterCost(character, isUnicode), 0);
}

function countMessages(costs, singleCapacity, concatCapacity) {
  const total = costs.reduce((sum, cost) => sum + cost, 0);
  if (total <= singleCapacity) return 1;
  let messages = 1;
  let used = 0;
  costs.forEach(cost => {
    if (used + cost > concatCapacity) {
      messages += 1;
      used = cost;
    } else {
      used += cost;
    }
  });
  return messages;
}

export function analyzeSms(text) {
  if (!text) return EMPTY_ANALYSIS;
  const characters = Array.from(text);
  const specialCharacterCounts = new Map();
  characters.forEach(character => {
    if (!isGsm7Character(character)) {
      specialCharacterCounts.set(character, (specialCharacterCounts.get(character) ?? 0) + 1);
    }
  });
  const isUnicode = specialCharacterCounts.size > 0;
  const costs = characters.map(character => getCharacterCost(character, isUnicode));
  const messages = isUnicode
    ? countMessages(costs, UCS2_SINGLE_CAPACITY, UCS2_CONCAT_CAPACITY)
    : countMessages(costs, GSM7_SINGLE_CAPACITY, GSM7_CONCAT_CAPACITY);
  return {
    isUnicode,
    characters: costs.reduce((sum, cost) => sum + cost, 0),
    messages,
    specialCharacters: Array.from(specialCharacterCounts, ([character, count]) => ({
      character,
      count,
      replacement: PLAIN_TEXT_REPLACEMENTS[character] ?? null,
    })),
  };
}

// A single message holds more than each part of a split one (160 vs 153, 70 vs 67),
// because every part of a split message carries a header to rejoin them.
export function getSmsCharacterLimit(isUnicode, maxMessages = MAX_SMS_MESSAGES) {
  if (maxMessages <= 0) return 0;
  if (maxMessages === 1) return isUnicode ? UCS2_SINGLE_CAPACITY : GSM7_SINGLE_CAPACITY;
  return maxMessages * (isUnicode ? UCS2_CONCAT_CAPACITY : GSM7_CONCAT_CAPACITY);
}

export function hasPlainTextReplacements(text) {
  return Array.from(text ?? '').some(character => character in PLAIN_TEXT_REPLACEMENTS);
}

export function replaceWithPlainCharacters(text) {
  return Array.from(text ?? '')
    .map(character => PLAIN_TEXT_REPLACEMENTS[character] ?? character)
    .join('');
}

export function getBillableSmsText(body, consentText, includeConsent) {
  const messageBody = body ?? '';
  if (!includeConsent || !consentText || !messageBody.trim()) return messageBody;
  return `${messageBody}${CONSENT_SEPARATOR}${consentText}`;
}

// Offsets are UTF-16 based to match Quill's indexes, so an emoji spans two.
export function getSmsSpecialCharacterRanges(text) {
  const ranges = [];
  let index = 0;
  Array.from(text ?? '').forEach(character => {
    if (!isGsm7Character(character)) ranges.push({ index, length: character.length });
    index += character.length;
  });
  return ranges;
}
